/* eslint-disable camelcase */
import Decimal from 'decimal.js';
import type { AccountDTO as Account, SiteAccountDTO, OwnedAccountDTO } from '@hotjar/account';
import type { PricingTables, PricingTable, PricingTableDTO } from './types';

type RawPricingTables = { [version: string]: PricingTableDTO };

export function sortPricingTable(table: PricingTable): [string, Decimal][] {
  return Object.entries(table).sort((a, b) => {
    const [, aCost] = a;
    const [, bCost] = b;
    return (aCost as Decimal).gt(bCost) ? 1 : -1;
  });
}

const FALLBACK_PRICING_TABLES = {
  '0': {
    '0': new Decimal(0),
  },
};

export class PricingRepository {
  private pricingTables: PricingTables;

  constructor(rawPricingTables: RawPricingTables) {
    this.pricingTables = this.convertPricingTablesValuesToDecimals(rawPricingTables);
  }

  byVersion(version = '0'): PricingTable {
    if (this.pricingTables[version]) {
      return { ...this.pricingTables[version] };
    }

    // This happens when the settings endpoint is still loading and we don't have pricing tables yet. So we use the
    // default one we return in the `convertPricingTablesValuesToDecimals` function.
    return { ...this.pricingTables['0'] };
  }

  byAccount(account: Account | SiteAccountDTO | OwnedAccountDTO | undefined): PricingTable {
    const { pricing_table_id } = account ?? {};
    const version = pricing_table_id ?? '0';
    return this.byVersion(version);
  }

  // eslint-disable-next-line class-methods-use-this
  private convertPricingTablesValuesToDecimals = (
    pricingTables: RawPricingTables,
  ): PricingTables => {
    // This happens when the settings endpoint is still loading and we don't have pricing tables yet.
    if (!pricingTables) {
      return FALLBACK_PRICING_TABLES;
    }

    // go through each table's key/value pairs array, convert the values to Decimal and return the table as an object again
    return Object.keys(pricingTables).reduce(
      (result, table) => ({
        ...result,
        [table]: Object.fromEntries(
          Object.entries(pricingTables[table]).map(([key, value]) => [key, new Decimal(value)]),
        ),
      }),
      {} as PricingTables,
    );
  };
}
